/**
 * CredentialsPage Component
 * Manages passkey credentials for the landlord/manager
 */

import { useState, useEffect, useCallback } from 'react';
import {
  startRegistration,
  browserSupportsWebAuthn,
} from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { KeyRound } from 'lucide-react';
import { Modal } from '../components/Modal';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { ApiClientError, triggerReauthRequired } from '../lib/api';
import { SettingsLayout } from '../components/settings/SettingsLayout';
import { EmptyState } from '../components/layout/index';
import { formatDateTime } from '../utils/date';

// Types
interface Credential {
  id: string;
  deviceName: string;
  createdAt: string;
  lastUsedAt: string | null;
  isActive: boolean;
}

interface RegistrationOptions {
  options: PublicKeyCredentialCreationOptionsJSON;
}

// API Base URL — must match backend route prefix: /{audience}/api
const API_BASE = '/landlord/api';

/**
 * Fetch with credentials and proper headers
 */
async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    credentials: 'include',
    headers: {
      // Only set Content-Type for requests with a body — Fastify rejects
      // DELETE/GET requests that declare application/json but send an empty body.
      ...(options.body != null ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    if (response.status === 403 && data.code === 'REAUTHENTICATION_REQUIRED') {
      triggerReauthRequired();
      return new Promise<never>(() => {}); // never resolves — caller won't execute
    }

    throw new ApiClientError(
      data.message || 'An error occurred',
      data.code || 'UNKNOWN_ERROR',
      response.status,
      data
    );
  }

  return data as T;
}

export function CredentialsPage() {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState<Credential | null>(null);
  const [newDeviceName, setNewDeviceName] = useState('');
  const [editDeviceName, setEditDeviceName] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Check WebAuthn support
  const webAuthnSupported = browserSupportsWebAuthn();

  const fetchCredentials = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiFetch<{ credentials: Credential[] }>('/credentials');
      setCredentials(response.credentials);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load credentials');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);

  const handleAddCredential = async () => {
    if (!webAuthnSupported) {
      setModalError('WebAuthn is not supported on this device');
      return;
    }

    try {
      setIsProcessing(true);
      setModalError(null);

      // Step 1: Get registration options (stores registration challenge)
      const { options } = await apiFetch<RegistrationOptions>('/credentials/options', {
        method: 'POST',
        body: JSON.stringify({}),
      });

      // Step 2: Start WebAuthn registration
      const registrationResponse = await startRegistration({ optionsJSON: options });

      // Step 3: Complete registration
      await apiFetch<{ credential: Credential }>('/credentials', {
        method: 'POST',
        body: JSON.stringify({
          response: registrationResponse,
          deviceName: newDeviceName || undefined,
        }),
      });

      setNewDeviceName('');
      setIsAddModalOpen(false);
      await fetchCredentials();
    } catch (err) {
      if (err instanceof Error) {
        // Handle user cancellation
        if (err.name === 'NotAllowedError') {
          setModalError('Registration was cancelled or timed out');
        } else {
          setModalError(err.message);
        }
      } else {
        setModalError('Failed to add credential');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpdateCredential = async () => {
    if (!selectedCredential || !editDeviceName.trim()) return;

    try {
      setIsProcessing(true);
      setModalError(null);

      await apiFetch<{ credential: Credential }>(`/credentials/${selectedCredential.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ deviceName: editDeviceName.trim() }),
      });

      setIsEditModalOpen(false);
      setSelectedCredential(null);
      setEditDeviceName('');
      await fetchCredentials();
    } catch (err) {
      setModalError(err instanceof Error ? err.message : 'Failed to update credential');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeleteCredential = async () => {
    if (!selectedCredential) return;

    try {
      setIsProcessing(true);
      setModalError(null);

      await apiFetch<{ success: boolean }>(`/credentials/${selectedCredential.id}`, {
        method: 'DELETE',
      });

      setIsDeleteModalOpen(false);
      setSelectedCredential(null);
      await fetchCredentials();
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'LAST_CREDENTIAL') {
        setModalError('Cannot remove your only passkey. Add another passkey first.');
      } else if (err instanceof Error) {
        if (err.name === 'NotAllowedError') {
          setModalError('Authentication was cancelled or timed out');
        } else {
          setModalError(err.message);
        }
      } else {
        setModalError('Failed to remove credential');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const openEditModal = (credential: Credential) => {
    setSelectedCredential(credential);
    setEditDeviceName(credential.deviceName);
    setModalError(null);
    setIsEditModalOpen(true);
  };

  const openDeleteModal = (credential: Credential) => {
    setSelectedCredential(credential);
    setModalError(null);
    setIsDeleteModalOpen(true);
  };

  if (isLoading) {
    return (
      <SettingsLayout>
        <div className="px-[var(--page-padding-x)]">
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="flex items-center justify-between mb-6">
              <div className="space-y-2">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-4 w-64" />
              </div>
              <Skeleton className="h-10 w-36" />
            </div>
            {[1, 2].map((i) => (
              <Skeleton key={i} className="h-20 w-full rounded-lg" />
            ))}
          </div>
        </div>
      </SettingsLayout>
    );
  }

  const canDelete = credentials.filter(c => c.isActive).length > 1;

  return (
    <SettingsLayout>
    <div className="px-[var(--page-padding-x)]">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-foreground m-0">Passkeys</h1>
              <p className="text-sm text-muted-foreground mt-1 mb-0">
                Manage the passkeys used to sign in to your account
              </p>
            </div>
            <Button
              onClick={() => {
                setModalError(null);
                setNewDeviceName('');
                setIsAddModalOpen(true);
              }}
              disabled={!webAuthnSupported}
              className="min-h-[44px]"
            >
              Add New Passkey
            </Button>
          </div>

          {!webAuthnSupported && (
            <div className="p-3 bg-accent/50 border border-border rounded-lg mb-6">
              <p className="m-0 text-sm text-muted-foreground">
                WebAuthn is not supported on this device or browser. You cannot add new passkeys.
              </p>
            </div>
          )}

          {error && (
            <div role="alert" className="flex justify-between items-center p-3 bg-destructive/5 border border-destructive/20 rounded-lg mb-6">
              <p className="text-sm text-destructive m-0">
                {error}
              </p>
              <Button
                variant="destructive"
                size="sm"
                onClick={fetchCredentials}
                className="min-h-[44px]"
              >
                Retry
              </Button>
            </div>
          )}

          {credentials.length === 0 ? (
            <div className="bg-card rounded-lg border border-border">
              <EmptyState
                icon={KeyRound}
                title="No passkeys yet"
                description="Passkeys let you sign in instantly with your face, fingerprint, or device PIN — no password to type or remember, and unlike a password they can't be phished or reused in a data breach."
                data-testid="no-passkeys-empty-state"
                action={
                  <Button
                    onClick={() => {
                      setModalError(null);
                      setNewDeviceName('');
                      setIsAddModalOpen(true);
                    }}
                    disabled={!webAuthnSupported}
                    className="min-h-[44px]"
                  >
                    Add Your First Passkey
                  </Button>
                }
              />
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {credentials.map((credential) => (
                <div
                  key={credential.id}
                  className="flex justify-between items-center px-5 py-4 bg-card rounded-lg border border-border"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 bg-muted rounded-xl text-muted-foreground">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </div>
                    <div className="flex flex-col">
                      <h3 className="m-0 text-base font-medium text-foreground">
                        {credential.deviceName}
                      </h3>
                      <p className="m-0 mt-0.5 text-sm text-muted-foreground">
                        Created: {formatDateTime(credential.createdAt, { year: 'numeric', month: 'short', day: 'numeric' })}
                      </p>
                      {credential.lastUsedAt && (
                        <p className="m-0 mt-0.5 text-sm text-muted-foreground">
                          Last used: {formatDateTime(credential.lastUsedAt, { year: 'numeric', month: 'short', day: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openEditModal(credential)}
                      title="Edit device name"
                      className="min-h-[44px]"
                    >
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => openDeleteModal(credential)}
                      disabled={!canDelete}
                      title={canDelete ? 'Remove passkey' : 'Cannot remove your only passkey'}
                      className="min-h-[44px]"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Add Credential Modal */}
          <Modal
            isOpen={isAddModalOpen}
            onClose={() => {
              if (!isProcessing) {
                setIsAddModalOpen(false);
                setNewDeviceName('');
                setModalError(null);
              }
            }}
            title="Add New Passkey"
          >
            <div>
              {modalError && (
                <div className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-sm text-destructive">
                  {modalError}
                </div>
              )}
              <p className="m-0 mb-4 text-sm text-foreground leading-relaxed">
                You will be prompted to register your new passkey on this device.
              </p>
              <div className="mb-5">
                <label
                  htmlFor="deviceName"
                  className="block mb-2 text-sm font-medium text-muted-foreground"
                >
                  Device Name (optional)
                </label>
                <input
                  id="deviceName"
                  type="text"
                  value={newDeviceName}
                  onChange={(e) => setNewDeviceName(e.target.value)}
                  placeholder="e.g., MacBook Pro, iPhone"
                  className="w-full min-h-[44px] px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
                  disabled={isProcessing}
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setNewDeviceName('');
                    setModalError(null);
                  }}
                  disabled={isProcessing}
                  className="min-h-[44px]"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAddCredential}
                  disabled={isProcessing}
                  className="min-h-[44px]"
                >
                  {isProcessing ? 'Processing...' : 'Continue'}
                </Button>
              </div>
            </div>
          </Modal>

          {/* Edit Credential Modal */}
          <Modal
            isOpen={isEditModalOpen}
            onClose={() => {
              if (!isProcessing) {
                setIsEditModalOpen(false);
                setSelectedCredential(null);
                setEditDeviceName('');
                setModalError(null);
              }
            }}
            title="Edit Passkey"
          >
            <form onSubmit={(e) => { e.preventDefault(); handleUpdateCredential(); }}>
              {modalError && (
                <div className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-sm text-destructive">
                  {modalError}
                </div>
              )}
              <div className="mb-5">
                <label
                  htmlFor="editDeviceName"
                  className="block mb-2 text-sm font-medium text-muted-foreground"
                >
                  Device Name
                </label>
                <input
                  id="editDeviceName"
                  type="text"
                  value={editDeviceName}
                  onChange={(e) => setEditDeviceName(e.target.value)}
                  placeholder="e.g., MacBook Pro, iPhone"
                  className="w-full min-h-[44px] px-3 py-2.5 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
                  disabled={isProcessing}
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsEditModalOpen(false);
                    setSelectedCredential(null);
                    setEditDeviceName('');
                    setModalError(null);
                  }}
                  disabled={isProcessing}
                  className="min-h-[44px]"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isProcessing || !editDeviceName.trim()}
                  className="min-h-[44px]"
                >
                  {isProcessing ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          </Modal>

          {/* Delete Confirmation Modal */}
          <Modal
            isOpen={isDeleteModalOpen}
            onClose={() => {
              if (!isProcessing) {
                setIsDeleteModalOpen(false);
                setSelectedCredential(null);
                setModalError(null);
              }
            }}
            title="Remove Passkey"
          >
            <div>
              {modalError && (
                <div className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-sm text-destructive">
                  {modalError}
                </div>
              )}
              <p className="m-0 mb-4 text-sm text-foreground leading-relaxed">
                Are you sure you want to remove "{selectedCredential?.deviceName}"?
              </p>
              <p className="m-0 mb-4 p-3 text-sm text-muted-foreground bg-accent/50 rounded-md leading-relaxed">
                You will no longer be able to sign in with this passkey.
              </p>
              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setSelectedCredential(null);
                    setModalError(null);
                  }}
                  disabled={isProcessing}
                  className="min-h-[44px]"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteCredential}
                  disabled={isProcessing}
                  className="min-h-[44px]"
                >
                  {isProcessing ? 'Processing...' : 'Remove Passkey'}
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      </div>
    </SettingsLayout>
  );
}

export default CredentialsPage;
